import { skipToken, useQuery } from '@tanstack/react-query';
import { usePropertyStore } from '@shared/stores/propertyStore';
import { invokeLinkAction, type UsageActionData } from './api';
import type { UsageActionUiMeta } from './utilityActionMeta';

const USAGE_ACTION_KEY = 'get_usage';

export interface UsageFetchOptions {
  registerCodes?: string[];
}

async function fetchUsage(
  linkId: string,
  timeframe: string,
  options?: UsageFetchOptions,
): Promise<UsageActionData> {
  const parameters: Record<string, unknown> = { timeframe };
  if (options?.registerCodes?.length) {
    parameters.register_codes = options.registerCodes;
  }
  const res = await invokeLinkAction<UsageActionData>(linkId, USAGE_ACTION_KEY, parameters);
  if (!res.success || !res.data) {
    throw new Error(res.message ?? 'Could not load usage');
  }
  return res.data;
}

export function utilityUsageQueryKey(
  linkId: string,
  timeframe: string,
  activePropertyId?: string | null,
  registerCodes?: string[],
): readonly [string, string, string, string | null | undefined, string] {
  const registerKey = registerCodes?.length ? registerCodes.join(',') : '';
  return ['utility-link-usage', linkId, timeframe, activePropertyId ?? null, registerKey];
}

export function useUtilityLinkUsage(
  linkId: string | undefined,
  timeframe: string,
  options?: { enabled?: boolean; registerCodes?: string[] },
) {
  const activePropertyId = usePropertyStore((s) => s.activePropertyId);
  const registerCodes = options?.registerCodes;

  return useQuery({
    queryKey: utilityUsageQueryKey(linkId ?? '', timeframe, activePropertyId, registerCodes),
    queryFn: linkId ? () => fetchUsage(linkId, timeframe, { registerCodes }) : skipToken,
    enabled: (options?.enabled ?? true) && !!linkId && !!timeframe,
    staleTime: 60_000,
  });
}

export function useUtilityLinkUsageFromMeta(
  linkId: string | undefined,
  meta: UsageActionUiMeta | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!linkId && !!meta;
  const registerCodes = meta?.defaultRegisterCodes;

  const usagePrimary = useUtilityLinkUsage(linkId, meta?.homeWidget.timeframe ?? '', {
    enabled,
    registerCodes,
  });
  const usageComparison = useUtilityLinkUsage(linkId, meta?.homeWidget.comparisonTimeframe ?? '', {
    enabled: enabled && !!meta?.homeWidget.comparisonTimeframe,
    registerCodes,
  });

  return {
    usagePrimary,
    usageComparison,
    isLoading: usagePrimary.isLoading || usageComparison.isLoading,
    isError: usagePrimary.isError || usageComparison.isError,
  };
}
